import { pipe, Option, Array as A } from 'effect';

import type { PayingAccountField, PayingAccountFieldOption } from '@/domain/paySettings/schemas';

export type FormValues = Record<string, string>;

export const readString = (values: Record<string, unknown>, name: string): string =>
  pipe(
    Option.fromNullable(values[name]),
    Option.filter((value): value is string => typeof value === 'string'),
    Option.getOrElse(() => ''),
  );

export const getParentFieldName = (field: PayingAccountField): string | undefined => field.dependsOn?.[0];

// No parent → every option; parent not chosen yet → none.
export const getScopedOptions = (
  field: PayingAccountField,
  values: Record<string, unknown>,
): PayingAccountFieldOption[] => {
  const options = field.options ?? [];

  return pipe(
    Option.fromNullable(getParentFieldName(field)),
    Option.match({
      onNone: () => options,
      onSome: (parentName) =>
        pipe(
          Option.some(readString(values, parentName)),
          Option.filter((parentValue) => parentValue.length > 0),
          Option.map((parentValue) =>
            pipe(
              options,
              A.filter((option) => option[parentName] === parentValue),
            ),
          ),
          Option.getOrElse<PayingAccountFieldOption[]>(() => []),
        ),
    }),
  );
};

// Bank options read "<name> (<code>)", e.g. "Leumi (010)".
export const getOptionDisplayLabel = (option: PayingAccountFieldOption): string =>
  pipe(
    Option.fromNullable(option.bank_code),
    Option.filter((code) => code.length > 0),
    Option.match({
      onNone: () => option.label,
      onSome: (code) => `${option.label} (${code})`,
    }),
  );

export const getDependentFieldNames = (fields: PayingAccountField[], parentName: string): string[] =>
  pipe(
    fields,
    A.filter((field) => getParentFieldName(field) === parentName),
    A.map((field) => field.name),
  );

// A part that is not a form field (`bank_code`) is read from the selected option.
export const resolveComputedPart = (
  partName: string,
  fields: PayingAccountField[],
  values: Record<string, unknown>,
): string =>
  pipe(
    fields,
    A.findFirst((field) => field.name === partName),
    Option.match({
      onSome: () => readString(values, partName),
      onNone: () =>
        pipe(
          fields,
          A.filterMap((field) =>
            pipe(
              Option.fromNullable(field.options),
              Option.flatMap(A.findFirst((option) => option.value === readString(values, field.name))),
              Option.flatMap((option) => Option.fromNullable(option[partName])),
            ),
          ),
          A.head,
          Option.getOrElse(() => ''),
        ),
    }),
  );
